import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  moduleDisplayName: string;
  onConfirm: () => void;
  pending?: boolean;
}

/**
 * High-friction confirmation. Requires the learner to explicitly acknowledge
 * before enabling the destructive action.
 */
export default function RetakeModuleDialog({
  open,
  onOpenChange,
  moduleDisplayName,
  onConfirm,
  pending,
}: Props) {
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (!open) setAcknowledged(false);
  }, [open]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Retake {moduleDisplayName}?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                This will reset your progress for this module and remove any money, rewards, or
                completion bonuses earned from it. You will need to complete the module again to
                earn them back.
              </p>
              <p className="font-semibold text-destructive">This cannot be undone.</p>
              <label className="mt-2 flex items-start gap-2 text-foreground">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  aria-label="I understand my rewards will be removed"
                />
                <span>
                  I understand my {moduleDisplayName} rewards will be removed. Other modules and
                  account rewards will not be affected.
                </span>
              </label>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={pending || !acknowledged}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {pending ? "Resetting…" : "Retake Module and Reset Rewards"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}